import {
  Avatar,
  FormControl,
  IconButton,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import React, { useCallback, useRef } from 'react';

import {
  AccessControlEnum,
  useGetDomainUsersQuery,
  UserStatus,
  useUpdateDomainUserMutation,
} from '../../../../graphql/gen/types';
import { TextSizesEnum } from '../../../../theme';
import { colorPalette } from '../../../../theme';
import { PlexTypography } from '../../../shared/primitives/PlexTypography';
import { FooterLoader } from '../../feed/FooterLoader';
import { FetchMoreCallback, useInfiniteScroll } from '../../feed/useInfiniteScroll';
import { SettingsContent } from '../SettingsContent';

function userStatusToString(status: UserStatus | null | undefined): string {
  switch (status) {
    case UserStatus.Active:
      return 'Active';
    case UserStatus.Invited:
      return 'Invited';
    case UserStatus.Disabled:
      return 'Disabled';
    default:
      return '';
  }
}

export const Team = (): React.ReactElement | null => {
  const { data, loading: initialLoading, fetchMore } = useGetDomainUsersQuery({ variables: {} });
  const [updateDomainUser, { loading: updateDomainUserLoading }] = useUpdateDomainUserMutation();

  const classes = useStyles();
  const userContainerRef = useRef<HTMLTableSectionElement>(null);

  const users = data?.currentUser?.domain?.users?.users;
  const nextCursor = data?.currentUser?.domain?.users?.nextCursor;

  const onFetchMore: FetchMoreCallback = useCallback(
    (isCancelled) =>
      fetchMore({
        variables: { cursor: nextCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult.currentUser?.domain?.users?.users || isCancelled()) return prev;
          return {
            ...prev,
            currentUser: {
              ...prev.currentUser,
              id: fetchMoreResult.currentUser?.id,
              domain: {
                ...prev.currentUser?.domain,
                id: fetchMoreResult.currentUser?.domain?.id,
                users: {
                  ...prev.currentUser?.domain?.users,
                  users: [
                    ...(prev.currentUser?.domain?.users?.users || []),
                    ...fetchMoreResult.currentUser?.domain?.users?.users,
                  ],
                  nextCursor: fetchMoreResult.currentUser?.domain?.users?.nextCursor,
                },
              },
            },
          };
        },
      }),
    [fetchMore, nextCursor]
  );
  const infiniteScrollLoading = useInfiniteScroll({
    containerRef: userContainerRef,
    onFetchMore,
    canFetchMore: !initialLoading && nextCursor != null,
  });

  const currentUserId = data?.currentUser?.id;

  const modifyUserStatus = (variables: {
    userId: string;
    status?: UserStatus;
    accessControl?: AccessControlEnum;
  }): void => {
    updateDomainUser({ variables });
  };

  const userStatusToClass = (status: UserStatus | null | undefined): string | undefined => {
    switch (status) {
      case UserStatus.Disabled:
        return classes.disabled;
      default:
        return undefined;
    }
  };

  return (
    <SettingsContent title={'Team'} hideActions updateDisabled={false} loadingMutation={false}>
      {!initialLoading && !!users && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.avatarColumn}>{/* avatar column */}</TableCell>
              <TableCell>
                <PlexTypography text={'NAME'} size={TextSizesEnum.XSmall} />
              </TableCell>
              <TableCell className={classes.column}>
                <PlexTypography text={'JOB TITLE'} size={TextSizesEnum.XSmall} />
              </TableCell>
              <TableCell className={classes.statusColumn}>
                <PlexTypography text={'STATUS'} size={TextSizesEnum.XSmall} />
              </TableCell>
              <TableCell className={classes.accessColumn}>
                <PlexTypography text={'ACCESS CONTROL'} size={TextSizesEnum.XSmall} />
              </TableCell>
              <TableCell className={classes.statusActionColumn}>{/* status action column */}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody ref={userContainerRef}>
            {users.map(({ name, id, picture, jobTitle, status, accessControl }) => (
              <TableRow key={id}>
                <TableCell className={classes.avatarColumn}>
                  <Avatar alt={name} src={picture} />
                </TableCell>
                <TableCell className={classes.column}>
                  <PlexTypography text={name} size={TextSizesEnum.Normal} className={userStatusToClass(status)} />
                </TableCell>
                <TableCell className={classes.column}>
                  <PlexTypography
                    text={jobTitle || 'Employee'}
                    size={TextSizesEnum.Normal}
                    className={userStatusToClass(status)}
                  />
                </TableCell>
                <TableCell className={classes.statusColumn}>
                  <PlexTypography
                    text={userStatusToString(status)}
                    size={TextSizesEnum.Normal}
                    className={userStatusToClass(status)}
                  />
                </TableCell>
                <TableCell className={classes.accessColumn}>
                  <FormControl disabled={id === currentUserId}>
                    <Select
                      aria-label={'Role'}
                      variant={'outlined'}
                      value={accessControl}
                      classes={{ root: classes.roleSelector }}
                      onChange={(event: React.ChangeEvent<{ name?: string; value: unknown }>): void => {
                        const accessControl = event.target.value as AccessControlEnum;
                        modifyUserStatus({ userId: id, accessControl });
                      }}
                      disabled={updateDomainUserLoading || id === currentUserId || status === UserStatus.Disabled}
                    >
                      <MenuItem value={AccessControlEnum.DomainAdmin}>Admin</MenuItem>
                      <MenuItem value={AccessControlEnum.User}>Member</MenuItem>
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell className={classes.statusActionColumn}>
                  {status !== UserStatus.Disabled && id !== currentUserId && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      disabled={updateDomainUserLoading}
                      onClick={(): void => modifyUserStatus({ userId: id, status: UserStatus.Disabled })}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                  {status === UserStatus.Disabled && id !== currentUserId && (
                    <IconButton
                      edge="end"
                      aria-label="restore"
                      disabled={updateDomainUserLoading}
                      onClick={(): void => modifyUserStatus({ userId: id, status: UserStatus.Active })}
                    >
                      <RestoreFromTrashIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            <FooterLoader isLoading={initialLoading || infiniteScrollLoading} />
          </TableBody>
        </Table>
      )}
    </SettingsContent>
  );
};

const useStyles = makeStyles((theme) => ({
  avatarColumn: {
    width: '12px',
  },
  statusColumn: {
    width: '100px',
  },
  column: {
    minWidth: '12px',
  },
  statusActionColumn: {
    width: '12px',
  },
  accessColumn: {
    width: '50px',
  },
  roleSelector: {
    paddingTop: 6,
    paddingBottom: 6,
  },
  disabled: {
    color: colorPalette.gray._400,
  },
}));
